import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useFcmRegistration } from "@/modules/notifications/FcmRegistrationProvider";

/**
 * Account-area row surfacing the current push-registration status.
 *
 * It reflects BOTH the per-browser notification permission AND the
 * per-(seller + store) backend registration owned by the
 * {@link FcmRegistrationProvider}. Placed in the account/profile area (not store
 * settings) because that pairing fits the account context — see the Phase-4
 * decision in the implementation plan.
 *
 * Must be rendered inside `<FcmRegistrationProvider>` (it is — account routes
 * render within AppShell, which mounts the provider).
 */
export function PushStatusRow() {
  const t = useTranslations();
  const { status, lastError, lastRegisteredAt, retry, requestPermissionAndRegister } =
    useFcmRegistration();

  // Derive a single, human-readable presentation from status (+ lastError for
  // the `error`/`config` nuance). `tone` only steers the status-dot colour.
  let label: string;
  let helper: string | null = null;
  let tone: "ok" | "pending" | "muted" | "error" = "muted";
  let action: { kind: "enable" | "retry" } | null = null;

  switch (status) {
    case "registered":
      label = t(keys.notifications.pushStatusEnabled);
      tone = "ok";
      break;
    case "registering":
      label = t(keys.notifications.pushStatusEnabling);
      tone = "pending";
      break;
    case "prompt":
      label = t(keys.notifications.pushStatusNotEnabled);
      tone = "muted";
      action = { kind: "enable" };
      break;
    case "denied":
      label = t(keys.notifications.pushStatusBlocked);
      helper = t(keys.notifications.pushBlockedHelp);
      tone = "error";
      break;
    case "offline":
      label = t(keys.notifications.pushStatusOffline);
      tone = "pending";
      action = { kind: "retry" };
      break;
    case "error":
      if (lastError?.reason === "config") {
        label = t(keys.notifications.pushStatusUnavailableDevice);
        helper = t(keys.notifications.unavailableConfigBody);
        tone = "error";
        // Retry is harmless on a terminal config error; offer it anyway.
        action = { kind: "retry" };
      } else {
        label = t(keys.notifications.pushStatusTrouble);
        tone = "error";
        action = { kind: "retry" };
      }
      break;
    case "unsupported":
      label = t(keys.notifications.pushStatusUnsupported);
      tone = "muted";
      break;
    default:
      label = t(keys.notifications.pushStatusEnabling);
      tone = "pending";
      break;
  }

  const showRegisteredAt = status === "registered" && lastRegisteredAt !== null;

  return (
    <div className="flex flex-col gap-3 rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
      <div className="min-w-0">
        <div className="text-[14px] font-semibold text-[color:var(--text-primary)]">
          {t(keys.notifications.pushRowTitle)}
        </div>
        <div className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
          {t(keys.notifications.pushRowScope)}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <StatusDot tone={tone} />
          <span className="text-[13px] font-medium text-[color:var(--text-secondary)]">
            {label}
          </span>
        </div>
        {showRegisteredAt ? (
          <div className="mt-1 text-[11.5px] text-[color:var(--text-muted)]">
            {t(keys.notifications.pushEnabledAt)}{" "}
            {formatRegisteredAt(lastRegisteredAt as number)}
          </div>
        ) : null}
        {helper ? (
          <div className="mt-1 text-[11.5px] leading-snug text-[color:var(--text-muted)]">
            {helper}
          </div>
        ) : null}
      </div>

      {action ? (
        <button
          type="button"
          onClick={() =>
            action.kind === "enable"
              ? void requestPermissionAndRegister()
              : retry()
          }
          className="inline-flex shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--border-strong)] bg-[color:var(--surface)] px-4 py-2 text-[13px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-alt)]"
        >
          {action.kind === "enable"
            ? t(keys.notifications.promptEnable)
            : t(keys.notifications.retry)}
        </button>
      ) : null}
    </div>
  );
}

function StatusDot({ tone }: { tone: "ok" | "pending" | "muted" | "error" }) {
  const color =
    tone === "ok"
      ? "var(--status-success, #16a34a)"
      : tone === "error"
        ? "var(--status-error, #dc2626)"
        : tone === "pending"
          ? "var(--accent-500)"
          : "var(--text-muted)";
  return (
    <span
      aria-hidden="true"
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ background: color }}
    />
  );
}

function formatRegisteredAt(ts: number): string {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  }).format(date);
}
